// @flow

import * as React from 'react';

import type { CalculationRequestProjectResult } from "../common/RequestTypes";

import ColorManager from "../common/ColorManager";
import type {Color} from "../common/ColorManager";
import OptionManager from "../common/OptionManager";
import ColorBadge from './ColorBadge';
import ReactAI from 'react-appinsights';

type Props = {
    calcResult: CalculationRequestProjectResult,
    optionManager: OptionManager,
};


class ClimateEffectOverviewPerPhaseTable extends React.Component<Props> {

    colorManager: ColorManager;

    constructor() {
        super();
        this.colorManager = new ColorManager();
    }

    render() {

        const hasIfl = this.props.calcResult.totalResult.totalDetailsKgCO2.insertionFlexibleLining.totalDerived !== 0;
        const hasTc = this.props.calcResult.totalResult.totalDetailsKgCO2.tubeCracking.totalDerived !== 0;
        const hasDd = this.props.calcResult.totalResult.totalDetailsKgCO2.directedDrilling.totalDerived !== 0;
        const hasSl = this.props.calcResult.totalResult.totalDetailsKgCO2.slipLining.totalDerived !== 0;
        const hasPul = this.props.calcResult.totalResult.totalDetailsKgCO2.puLiner.totalDerived !== 0;
        const indent = <span>&nbsp;&nbsp;&nbsp;</span>;

        return (
            <table className="table table-borderless table-sm">
                <thead>
                <tr>
                    <th></th>
                    <th></th>
                    { this.props.calcResult.phaseResults.map((phaseResult, idx) => <th key={idx}>Etapp {idx + 1} (ton CO<sub>2</sub>e)</th>) }
                </tr>
                </thead>
                <tbody>
                <tr>
                    <th scope="row">{this.colorBadge(this.colorManager.colorTraditionalDigging)} </th>
                    <th scope="row">Traditionell grävning</th>
                    { this.props.calcResult.phaseResults.map((phaseResult, idx) => <td key={idx}>{ this.round(phaseResult.totalTraditionalDiggingTonCO2, 1) }</td>) }
                </tr>
                <tr className="light-shade">
                    <th></th>
                    <th scope="row">NoDig totalt</th>
                    { this.props.calcResult.phaseResults.map((phaseResult, idx) => <td key={idx}>{ this.round(phaseResult.totalNoDigTonCO2, 1) }</td>) }
                </tr>
                { hasIfl &&
                <tr className="light-shade">
                    <th scope="row">{this.colorBadge(this.colorManager.colorInsertionFlexibleLining)} </th>
                    <th scope="row" className="secondary-data">{indent}<small>Infodring flexibla foder</small></th>
                    { this.props.calcResult.phaseResults.map((phaseResult, idx) => <td key={idx}>{ this.round(phaseResult.totalDetailsKgCO2.insertionFlexibleLining.totalDerived / 1000, 1) }</td>) }
                </tr>
                }
                { hasTc &&
                <tr className="light-shade">
                    <th scope="row">{this.colorBadge(this.colorManager.colorTubeCracking)} </th>
                    <th scope="row" className="secondary-data">{indent}<small>Rörspräckning</small></th>
                    { this.props.calcResult.phaseResults.map((phaseResult, idx) => <td key={idx}>{ this.round(phaseResult.totalDetailsKgCO2.tubeCracking.totalDerived / 1000, 1) }</td>) }
                </tr>
                }
                { hasDd &&
                <tr className="light-shade">
                    <th scope="row">{this.colorBadge(this.colorManager.colorDirectedDrilling)} </th>
                    <th scope="row" className="secondary-data">{indent}<small>Styrd borrning</small></th>
                    { this.props.calcResult.phaseResults.map((phaseResult, idx) => <td key={idx}>{ this.round(phaseResult.totalDetailsKgCO2.directedDrilling.totalDerived / 1000, 1) }</td>) }
                </tr>
                }
                { hasSl &&
                <tr className="light-shade">
                    <th scope="row">{this.colorBadge(this.colorManager.colorSliplining)} </th>
                    <th scope="row" className="secondary-data">{indent}<small>Sliplining</small></th>
                    { this.props.calcResult.phaseResults.map((phaseResult, idx) => <td key={idx}>{ this.round(phaseResult.totalDetailsKgCO2.slipLining.totalDerived / 1000, 1) }</td>) }
                </tr>
                }
                { hasPul &&
                <tr className="light-shade">
                    <th scope="row">{this.colorBadge(this.colorManager.colorPuLiner)} </th>
                    <th scope="row" className="secondary-data">{indent}<small>PU liner</small></th>
                    { this.props.calcResult.phaseResults.map((phaseResult, idx) => <td key={idx}>{ this.round(phaseResult.totalDetailsKgCO2.puLiner.totalDerived / 1000, 1) }</td>) }
                </tr>
                }

                <tr>
                    <th scope="row"></th>
                    <th scope="row">Lägre utsläpp NoDig</th>
                    { this.props.calcResult.phaseResults.map((phaseResult, idx) => <td key={idx}>{ this.round((phaseResult.totalTraditionalDiggingTonCO2 - phaseResult.totalNoDigTonCO2) / phaseResult.totalTraditionalDiggingTonCO2 * 100, 0) } %</td>) }
                </tr>
                
                </tbody>
            </table>
        );

    }

    round(value: number, numDecimals: number): string {
        if(value === 0) {
            return "-";
        }
        else {
            let rounded = parseFloat(value.toFixed(numDecimals));
            if(this.detectIE() === false)
                return rounded.toLocaleString("se-SV"); // Not OK in IE 10 and 11
            else
                return rounded.toString().replace(".", ",");
        }
    }

    detectIE() {
        let ua = window.navigator.userAgent;

        // Test values; Uncomment to check result …

        // IE 10
        // ua = 'Mozilla/5.0 (compatible; MSIE 10.0; Windows NT 6.2; Trident/6.0)';

        // IE 11
        // ua = 'Mozilla/5.0 (Windows NT 6.3; Trident/7.0; rv:11.0) like Gecko';

        // Edge 12 (Spartan)
        // ua = 'Mozilla/5.0 (Windows NT 10.0; WOW64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/39.0.2171.71 Safari/537.36 Edge/12.0';

        // Edge 13
        // ua = 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/46.0.2486.0 Safari/537.36 Edge/13.10586';

        let msie = ua.indexOf('MSIE ');
        if (msie > 0) {
            // IE 10 or older => return version number
            return parseInt(ua.substring(msie + 5, ua.indexOf('.', msie)), 10);
        }

        let trident = ua.indexOf('Trident/');
        if (trident > 0) {
            // IE 11 => return version number
            let rv = ua.indexOf('rv:');
            return parseInt(ua.substring(rv + 3, ua.indexOf('.', rv)), 10);
        }

        let edge = ua.indexOf('Edge/');
        if (edge > 0) {
            // Edge (IE 12+) => return version number
            return parseInt(ua.substring(edge + 5, ua.indexOf('.', edge)), 10);
        }

        // other browser
        return false;
    }


    colorBadge(color: Color) {
        let colorString = this.colorManager.getRgbaString(color);
        return <ColorBadge colorString={colorString} />
    }
}

export default ReactAI.withTracking(ClimateEffectOverviewPerPhaseTable); 







// WEBPACK FOOTER //
// ./src/components/ClimateEffectOverviewPerPhaseTable.js